import React, { useEffect, useState } from "react";
import "./worker_home.css";
import { User } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

/* ===== FIREBASE ===== */
import { db } from "../../firebase";
import { collection, query, where, getDocs, limit, documentId, onSnapshot, doc, getDoc } from "firebase/firestore";

/* ===== HELPERS ===== */
const getISOWeek = (date) => {
  const tmp = new Date(date.getTime());
  tmp.setHours(0, 0, 0, 0);
  tmp.setDate(tmp.getDate() + 3 - ((tmp.getDay() + 6) % 7));
  const week1 = new Date(tmp.getFullYear(), 0, 4);
  return (1 + Math.round(((tmp.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7));
};

const formatDateLT = (date) => {
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
};

const WorkerHomePage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const today = new Date();
  const kw = getISOWeek(today);

  const [workerName, setWorkerName] = useState(localStorage.getItem("workerName") || "");
  const [companyName, setCompanyName] = useState(localStorage.getItem("companyName") || "");
  const [unsignedCount, setUnsignedCount] = useState(0); 
  const workerId = localStorage.getItem("workerId");

  useEffect(() => {
    /* --- 1. SAUGUMO PATIKRA --- */
    const isRegistered = localStorage.getItem("workerDeviceRegistered") === "true";
    const hasSession = localStorage.getItem("workerGateDone") === "true";

    if (!isRegistered) { navigate("/worker", { replace: true }); return; }
    if (!hasSession || !workerId) { navigate("/worker/login", { replace: true }); return; }

    let unsubscribeDocs = null; // Kintamasis valymui

    /* --- 2. DARBUOTOJO DUOMENYS IR DOKUMENTŲ STEBĖJIMAS --- */
    const loadWorkerAndWatchDocs = async () => {
      try {
        const q = query(collection(db, "workers"), where(documentId(), "==", workerId), limit(1));
        const snap = await getDocs(q);

        if (!snap.empty) {
          const data = snap.docs[0].data();
          const foundName = data.name || "";
          const masterAdminId = data.adminId; 

          setWorkerName(foundName);
          localStorage.setItem("workerName", foundName);
          
          // === SVARBUS PATAISYMAS ===
          // Įrašome adminId, kad GlobalWorkerTracker jį rastų
          if (masterAdminId) {
              localStorage.setItem("adminId", masterAdminId);
              
              // MAGIJA: Priverstinai pranešame kitiems failams (Tracker'iui), kad localStorage pasikeitė
              window.dispatchEvent(new Event("storage"));
          }
          // ==========================

          // Paimame įmonės pavadinimą
          if (masterAdminId) {
            const adminRef = doc(db, "users", masterAdminId);
            const adminSnap = await getDoc(adminRef);
            
            if (adminSnap.exists()) {
              const adminData = adminSnap.data();
              const fetchedCompany = adminData.companyName || adminData.company || "";
              setCompanyName(fetchedCompany);
              localStorage.setItem("companyName", fetchedCompany);
            }
          }

          /* --- STEBIME DOKUMENTUS (Realiu laiku) --- */
          // Perkėlėme unsubscribe priskyrimą į išorinį kintamąjį
          const docsQuery = query(collection(db, "company_docs"), where("adminId", "==", masterAdminId));
          
          unsubscribeDocs = onSnapshot(docsQuery, (docSnap) => {
            let count = 0;
            docSnap.forEach(doc => {
              const d = doc.data();
              const isForMe = !d.assignedTo || d.assignedTo === 'all' || (Array.isArray(d.assignedTo) && d.assignedTo.includes(workerId));
              const notSigned = !d.signatures || !d.signatures.includes(workerId);

              if (isForMe && notSigned && d.requireSignature) {
                count++;
              }
            });

            // Čia naudojame funkcinį state atnaujinimą, kad išvengtume ciklo
            setUnsignedCount(prev => {
                if (count > prev && prev !== 0) {
                    if (navigator.vibrate) navigator.vibrate([200, 100, 200]); 
                    const audio = new Audio("https://assets.mixkit.co/active_storage/sfx/2358/2358-preview.mp3");
                    audio.play().catch(() => {}); 
                }
                return count;
            });
          });
        }
      } catch (e) { console.error("Klaida krovime:", e); }
    };

    loadWorkerAndWatchDocs();

    // --- IŠVALYMAS (Cleanup) ---
    // Tai labai svarbu! Kai išeini iš puslapio, išjungiame klausymąsi.
    return () => {
        if (unsubscribeDocs) {
            unsubscribeDocs();
        }
    };

  // Pašalinome 'unsignedCount' iš dependency masyvo, kad išvengtume begalinio ciklo
  }, [workerId, navigate]); 

  return (
    <div className="worker-home">
      <div className="worker-header">
        <div>
          {companyName && (
            <div style={{ fontSize: "0.85rem", opacity: 0.8, fontWeight: "500", marginBottom: "2px", textTransform: "uppercase" }}>
              {companyName}
            </div>
          )}
          <div className="worker-name">{workerName || t('worker')}</div>
          <div className="worker-date">{formatDateLT(today)} – KW {kw}</div>
        </div>
        <div className="worker-profile" onClick={() => navigate("/worker/profile")}>
          <User size={24} />
        </div>
      </div>

      <div className="worker-grid">
        <div className="tile tile-week" onClick={() => navigate("/worker/weekly")}>
          <span className="icon">📅</span>
          <span>{t('weekly_plan')}</span>
        </div>

        <div className="tile tile-start" onClick={() => navigate("/worker/start-work")}>
          <span className="icon">⏱️</span>
          <span>{t('start_work')}</span>
        </div>

        <div className="tile tile-hours" onClick={() => navigate("/worker/my-hours")}>
          <span className="icon">📊</span>
          <span>{t('workHours')}</span>
        </div>

        <div className="tile tile-docs" onClick={() => navigate("/worker/documents")}>
          <span className="icon">📄</span>
          <span>{t('documents')}</span>
          {unsignedCount > 0 && (
            <div className="doc-badge">{unsignedCount}</div>
          )}
        </div>

        <div className="tile tile-reports" onClick={() => navigate("/worker/reports")}>
          <span className="icon">📷</span>
          <span>{t('reports_title')}</span>
        </div>

        {/* UŽRAKINTOS FUNKCIJOS */}
        <div className="tile locked">
          <span className="icon">🚗</span>
          <span>{t('fahrtenbuch')}</span>
          <div className="lock">🔒</div>
        </div>
        <div className="tile locked">
          <span className="icon">💬</span>
          <span>{t('chat')}</span>
          <div className="lock">🔒</div>
        </div>
        <div className="tile locked">
          <span className="icon">📁</span>
          <span>{t('acts')}</span>
          <div className="lock">🔒</div>
        </div>
      </div>
    </div>
  );
};

export default WorkerHomePage;